import { ethers } from 'ethers6'; // Ethers v6 for our codebase
import { supabase } from './supabase';

// WETH contract address on Ink L2 (Canonical WETH)
const WETH_ADDRESS = '0x4200000000000000000000000000000000000006';

/**
 * Process creator earnings from a transaction receipt
 * Extracts WETH transfer amounts and stores in Supabase
 */
export async function processCreatorEarnings(
  txnHash: string,
  creatorAddress: string,
  nftTokenId: string
): Promise<{ success: boolean; wethEarned?: string; error?: string }> {
  try {
    console.log(`Processing creator earnings for transaction: ${txnHash}`);

    // Get transaction receipt
    const provider = new ethers.JsonRpcProvider(
      'https://old-young-fog.ink-mainnet.quiknode.pro/921654dbc67e6b99bcdf05a56d80d4c28858aba7/'
    );

    const receipt = await provider.getTransactionReceipt(txnHash);

    if (!receipt) {
      throw new Error('Transaction receipt not found');
    }

    if (receipt.status !== 1) {
      throw new Error('Transaction failed');
    }

    // Parse logs to find WETH transfers to the creator
    let totalWethEarned = '0';

    for (const log of receipt.logs) {
      // Check if this is a WETH transfer to the creator
      if (log.address.toLowerCase() === WETH_ADDRESS.toLowerCase()) {
        // Parse Transfer event (topic[0] = keccak256("Transfer(address,address,uint256)"))
        const transferTopic = '0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef';

        if (log.topics[0] === transferTopic && log.topics.length >= 3) {
          // Extract recipient address (topic[2])
          const recipient = '0x' + log.topics[2].slice(26); // Remove padding

          if (recipient.toLowerCase() === creatorAddress.toLowerCase()) {
            // Extract amount from data
            const amount = BigInt(log.data);
            totalWethEarned = (BigInt(totalWethEarned) + amount).toString();
          }
        }
      }
    }

    // Convert wei to decimal WETH for storage (human-readable format)
    const wethEarnedDecimal = (parseFloat(totalWethEarned) / Math.pow(10, 18)).toString();

    // Log if no WETH was found (still record the transaction)
    if (totalWethEarned === '0') {
      console.log('No WETH earnings found in transaction - recording 0 amount');
    } else {
      console.log(`💰 WETH earned: ${wethEarnedDecimal} WETH (${totalWethEarned} wei)`);
    }

    // Store decimal WETH value in Supabase (not wei)
    const { error: insertError } = await supabase
      .from('creator_earnings')
      .insert({
        creator_address: creatorAddress,
        nft_token_id: nftTokenId,
        txn_hash: txnHash,
        weth_earned: wethEarnedDecimal  // Store as decimal WETH
      });

    if (insertError) {
      console.error('Error storing creator earnings:', insertError);
      throw new Error(`Failed to store earnings: ${insertError.message}`);
    }

    console.log(`Successfully stored ${wethEarnedDecimal} WETH earnings for creator ${creatorAddress}`);

    return {
      success: true,
      wethEarned: wethEarnedDecimal
    };

  } catch (error) {
    console.error('Error processing creator earnings:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Format WETH amount for display
 */
export function formatWethAmount(amount: string): string {
  const numAmount = parseFloat(amount);

  if (numAmount === 0) return '0';
  if (numAmount < 0.001) return '< 0.001';
  if (numAmount < 1) return numAmount.toFixed(3);
  if (numAmount < 100) return numAmount.toFixed(2);
  return numAmount.toFixed(1);
}